/**
 * Barra de acción del cuadro (008 · T045 · US1; contracts/ui.md §1.2).
 *
 * Aparece fija abajo cuando hay celdas seleccionadas: contador ("12 días seleccionados"), qué
 * poner en ellas —"Asignar turno" (hasta 2 plantillas activas), "Descanso" o "Sin horario"—,
 * "Guardar" (un solo `POST /schedule-grid/cells`), "Ver historial" (con una sola celda) y
 * "Limpiar selección". Sin optimistic UI: el cuadro cambia cuando la API confirma.
 */
import { CalendarClock, Coffee, History, X, CircleSlash } from 'lucide-react';
import { useMemo, useState } from 'react';

import { SCHEDULE_GRID_MAX_CELLS, type DayPlanKind, type ShiftTemplate } from '@fermin/contracts';

import { Button } from '../../../components/ui/button';
import { Combobox, type ComboboxOption } from '../../../components/ui/combobox';
import { Field } from '../../../components/ui/field';
import { t } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { timeRange } from '../format';

/** Lo que se guarda en todas las celdas seleccionadas. */
export interface GridAction {
  kind: DayPlanKind;
  templateIds: string[];
}

export interface GridActionBarProps {
  count: number;
  templates: readonly ShiftTemplate[];
  pending: boolean;
  onSave: (action: GridAction) => void;
  onClear: () => void;
  /** Presente solo si hay exactamente una celda seleccionada. */
  onHistory?: (() => void) | undefined;
}

const NONE = '';

export function GridActionBar({
  count,
  templates,
  pending,
  onSave,
  onClear,
  onHistory,
}: GridActionBarProps) {
  const [kind, setKind] = useState<DayPlanKind>('SHIFT');
  const [first, setFirst] = useState(NONE);
  const [second, setSecond] = useState(NONE);

  const options: ComboboxOption[] = useMemo(
    () =>
      templates.map((template) => ({
        value: template.id,
        label: `${template.code} · ${template.name} (${timeRange(
          template.current.start_time,
          template.current.end_time,
        )})`,
        keywords: template.code,
      })),
    [templates],
  );
  const secondOptions: ComboboxOption[] = useMemo(
    () => [
      { value: NONE, label: t('schedules.grid.actions.noSecond') },
      ...options.filter((option) => option.value !== first),
    ],
    [options, first],
  );

  const tooMany = count > SCHEDULE_GRID_MAX_CELLS;
  const missingTemplate = kind === 'SHIFT' && first === NONE;

  function save() {
    const templateIds =
      kind === 'SHIFT' ? [first, second].filter((id) => id !== NONE && id !== undefined) : [];
    onSave({ kind, templateIds: [...new Set(templateIds)] });
  }

  const modes: { value: DayPlanKind; label: string; icon: typeof Coffee; testId: string }[] = [
    {
      value: 'SHIFT',
      label: t('schedules.grid.actions.assign'),
      icon: CalendarClock,
      testId: 'schedule-grid-assign',
    },
    {
      value: 'REST',
      label: t('schedules.grid.actions.rest'),
      icon: Coffee,
      testId: 'schedule-grid-rest',
    },
    {
      value: 'OFF',
      label: t('schedules.grid.actions.off'),
      icon: CircleSlash,
      testId: 'schedule-grid-off',
    },
  ];

  return (
    <div
      role="region"
      aria-label={t('schedules.grid.actions.label')}
      data-testid="schedule-grid-actionbar"
      className="sticky bottom-0 z-30 flex flex-col gap-fm-3 rounded-fm-lg border border-fm-border bg-fm-surface p-fm-4 shadow-fm-2"
    >
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        <p className="text-fm-sm font-semibold text-fm-text" aria-live="polite">
          {t('schedules.grid.actions.count', { count })}
        </p>
        <div className="flex flex-wrap items-center gap-fm-2">
          {onHistory ? (
            <Button
              variant="ghost"
              size="md"
              icon={<History size={20} />}
              data-testid="schedule-grid-history"
              onClick={onHistory}
            >
              {t('schedules.grid.actions.history')}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="md"
            icon={<X size={20} />}
            data-testid="schedule-grid-clear"
            onClick={onClear}
          >
            {t('schedules.grid.actions.clear')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-fm-3">
        <div
          role="group"
          aria-label={t('schedules.grid.actions.modeLabel')}
          className="flex flex-wrap gap-fm-2"
        >
          {modes.map((mode) => (
            <Button
              key={mode.value}
              variant="secondary"
              size="md"
              icon={<mode.icon size={20} />}
              aria-pressed={kind === mode.value}
              data-testid={mode.testId}
              className={cn(kind === mode.value && 'border-fm-primary bg-fm-primary-soft')}
              onClick={() => setKind(mode.value)}
            >
              {mode.label}
            </Button>
          ))}
        </div>

        {kind === 'SHIFT' ? (
          <>
            <Field label={t('schedules.grid.actions.firstShift')} className="w-[260px]">
              {(control) => (
                <Combobox
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={first}
                  onValueChange={(value) => {
                    setFirst(value);
                    if (value === second) {
                      setSecond(NONE);
                    }
                  }}
                  options={options}
                  placeholder={t('schedules.grid.actions.choose')}
                  searchPlaceholder={t('schedules.grid.actions.search')}
                  emptyMessage={t('schedules.grid.actions.noTemplates')}
                />
              )}
            </Field>
            <Field label={t('schedules.grid.actions.secondShift')} className="w-[260px]">
              {(control) => (
                <Combobox
                  id={control.id}
                  aria-describedby={control['aria-describedby']}
                  value={second}
                  onValueChange={setSecond}
                  options={secondOptions}
                  disabled={first === NONE}
                  placeholder={t('schedules.grid.actions.noSecond')}
                  searchPlaceholder={t('schedules.grid.actions.search')}
                />
              )}
            </Field>
          </>
        ) : null}

        <Button
          variant="compact"
          size="md"
          data-testid="schedule-grid-save"
          loading={pending}
          loadingText={t('common.states.saving')}
          disabled={tooMany || missingTemplate}
          onClick={save}
        >
          {t('schedules.grid.actions.save', { count })}
        </Button>
      </div>

      {tooMany ? (
        <p role="alert" className="text-fm-xs text-fm-danger-text">
          {t('schedules.grid.actions.tooMany', { max: SCHEDULE_GRID_MAX_CELLS })}
        </p>
      ) : null}
    </div>
  );
}
